import React from 'react';
import { Helmet } from 'react-helmet-async';
import { JsonLd, SeoMeta } from '@/components/JsonLd';
import { resolveImg } from './shared';

export const SeoHead = ({ siteSettings, metaTitle, metaDesc, metaKeywords, isEn, services, companyInfo, faqs }) => (
  <>
    <SeoMeta
      title={metaTitle || 'Solar GREEN s.r.o. | Fotovoltaické elektrárny a tepelná čerpadla'}
      description={metaDesc || 'Projektování, montáž a servis fotovoltaických elektráren, tepelných čerpadel a bateriových systémů. 400+ instalací, 15 let zkušeností. Kladno a celá ČR.'}
      canonical={siteSettings.site_url ? `${siteSettings.site_url}/` : 'https://www.solar-green.cz/'}
      hreflang={[
        ['cs', siteSettings.site_url ? `${siteSettings.site_url}/` : 'https://www.solar-green.cz/'],
        ['x-default', siteSettings.site_url ? `${siteSettings.site_url}/` : 'https://www.solar-green.cz/'],
      ]}
      og={{
        url: siteSettings.site_url ? `${siteSettings.site_url}/` : 'https://www.solar-green.cz/',
        type: 'website',
        title: metaTitle || 'Solar GREEN s.r.o. | Fotovoltaické elektrárny a tepelná čerpadla',
        description: metaDesc || 'Projektování, montáž a servis fotovoltaických elektráren, tepelných čerpadel a bateriových systémů.',
        image: siteSettings.og_image ? resolveImg(siteSettings.og_image) : '',
        site_name: 'Solar GREEN s.r.o.',
        locale: 'cs_CZ',
      }}
      extra={[
        ...(metaKeywords ? [['name', 'keywords', metaKeywords]] : [['name', 'keywords', 'fotovoltaika, solární panely, tepelná čerpadla, bateriové úložiště, elektroinstalace, Solar GREEN, Kladno, dotace NZÚ, fotovoltaická elektrárna, obnovitelné zdroje energie, montáž solárních panelů, chytrá domácnost']]),
        ['name', 'robots', 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'],
        ['name', 'author', 'Solar GREEN s.r.o.'],
        ...(siteSettings.google_verification ? [['name', 'google-site-verification', siteSettings.google_verification]] : []),
        ...(siteSettings.seznam_verification ? [['name', 'seznam-wmt', siteSettings.seznam_verification]] : []),
      ]}
    />
    <JsonLd data={{
      "@context": "https://schema.org", "@type": "LocalBusiness",
      "@id": `${siteSettings.site_url || 'https://www.solar-green.cz'}/#organization`,
      "name": "Solar GREEN s.r.o.", "alternateName": "Solar Green",
      "description": metaDesc || "Fotovoltaické elektrárny, tepelná čerpadla a bateriové systémy.",
      "url": siteSettings.site_url || 'https://www.solar-green.cz',
      "telephone": "+420 222 36 36 00", "email": "info@solar-green.cz", "foundingDate": "2010",
      "numberOfEmployees": { "@type": "QuantitativeValue", "minValue": 5, "maxValue": 20 },
      "address": { "@type": "PostalAddress", "streetAddress": "Huťská 368", "addressLocality": "Kladno", "postalCode": "27201", "addressRegion": "Středočeský kraj", "addressCountry": "CZ" },
      "geo": { "@type": "GeoCoordinates", "latitude": "50.1433", "longitude": "14.1025" },
      "openingHoursSpecification": [{ "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"], "opens": "08:00", "closes": "17:00" }],
      "priceRange": "$$",
      "image": siteSettings.og_image ? resolveImg(siteSettings.og_image) : "",
      "logo": siteSettings.og_image ? resolveImg(siteSettings.og_image) : "",
      "areaServed": [{ "@type": "Country", "name": "Česká republika" }, { "@type": "City", "name": "Kladno" }, { "@type": "City", "name": "Praha" }],
      "knowsAbout": ["fotovoltaika", "tepelná čerpadla", "bateriové systémy", "elektroinstalace", "dotace NZÚ", "Loxone chytrá domácnost"],
      "hasOfferCatalog": {
        "@type": "OfferCatalog", "name": "Služby Solar GREEN",
        "itemListElement": (services || []).slice(0, 6).map((s) => ({
          "@type": "Offer",
          "itemOffered": { "@type": "Service",
            "name": isEn ? (s.title_en || s.title_cs) : (s.title_cs || s.title_en),
            "description": isEn ? (s.short_desc_en || s.short_desc_cs) : (s.short_desc_cs || s.short_desc_en),
          }
        })),
      },
      "sameAs": [companyInfo?.instagram_url, companyInfo?.linkedin_url].filter(Boolean),
    }} />
    <JsonLd data={{
      "@context": "https://schema.org", "@type": "WebSite",
      "name": "Solar GREEN s.r.o.", "url": siteSettings.site_url || 'https://www.solar-green.cz',
      "inLanguage": ["cs", "en"],
      "potentialAction": {
        "@type": "SearchAction",
        "target": { "@type": "EntryPoint", "urlTemplate": `${siteSettings.site_url || 'https://www.solar-green.cz'}/blog?q={search_term_string}` },
        "query-input": "required name=search_term_string"
      },
    }} />
    <JsonLd data={{
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      "itemListElement": [{ "@type": "ListItem", "position": 1, "name": "Domů", "item": siteSettings.site_url || 'https://www.solar-green.cz' }]
    }} />
    {faqs && faqs.length > 0 && (
      <JsonLd data={{
        "@context": "https://schema.org", "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": isEn ? (faq.question_en || faq.question_cs) : (faq.question_cs || faq.question_en),
          "acceptedAnswer": { "@type": "Answer", "text": isEn ? (faq.answer_en || faq.answer_cs) : (faq.answer_cs || faq.answer_en) }
        })),
      }} />
    )}
  </>
);
